import { Field, FieldProps } from 'formik';
import React, { ReactNode } from 'react';
import { InjectedIntl, injectIntl } from 'react-intl';
import InputField from '../components/InputField';
import { TranslationHelpers } from '../../../i18n/translationHelpers';
import { CityFieldValidator } from '../validator/cityFieldValidator';
import { ValidatorRule } from '../validator/validatorRule.constants';
import { IErrorMessage } from '../components/errorMessage/errorMessage.type';
import TWCitiesMap from '../../../i18n/tw-states/tw-cities.json';
import SearchSelectImproved from '../components/SearchSelectImproved';
import ValueFormatters from './valueFormatters';
import { PostalCodeValidation } from './postalcode/PostalCodeValidation';

interface ICityFieldProps {
    autoFocus?: boolean;
    intl: InjectedIntl;
    value?: string;
    code?: string;
    required?: boolean;
    validatorRules?: ValidatorRule[];
    postalCode?: string;
    state?: string;
    countryCode?: string;
    className?: string;
}

interface ICity {
    code: string;
    name: string;
    postalCode: string;
}

interface ICityFieldState {
    resetValue: boolean;
}

class CityField extends React.Component<ICityFieldProps, ICityFieldState> {
    private readonly ID: string;
    private readonly LABEL_ID: string;
    private readonly ERROR_REQUIRED_ID: string;
    private readonly MAX_LENGTH: number;

    private cityFieldValidator: CityFieldValidator;

    private citiesMap?: Record<string, ICity[]>;

    constructor(props: ICityFieldProps) {
        super(props);

        this.ID = 'city';
        this.LABEL_ID = 'label.city';
        this.ERROR_REQUIRED_ID = 'error.city_required';
        this.MAX_LENGTH = 200;

        this.validate = this.validate.bind(this);

        const { validatorRules = [ValidatorRule.EUCityLengthRule] } = props;
        this.cityFieldValidator = new CityFieldValidator(validatorRules);
        this.resolveCitiesMap();
        this.renderField = this.renderField.bind(this);
        this.state = {
            resetValue: false,
        };
    }

    public componentDidUpdate(prevProps: ICityFieldProps) {
        const { countryCode } = this.props;
        if (prevProps.countryCode !== countryCode) {
            this.resolveCitiesMap();
        }
        if (this.props.state !== prevProps.state) {
            this.setState({
                resetValue: true,
            });
        }
    }

    public render() {
        return <Field name={this.ID} validate={this.validate} render={this.renderField} />;
    }

    private renderField(fieldProps: FieldProps) {
        const { setFieldValue } = fieldProps.form;
        if (this.state.resetValue) {
            setFieldValue('city', '');
            setFieldValue('cityCode', '');
            setFieldValue('postalCode', '');
            this.setState({ resetValue: false });
        }

        return this.citiesMap
            ? this.renderSelectField(fieldProps)
            : this.renderInputField(fieldProps);
    }

    private validate(value: string): IErrorMessage {
        const { intl, required, postalCode } = this.props;

        if (this.citiesMap && (!value || !this.props.code) && required) {
            return intl.formatMessage(
                TranslationHelpers.getMessageDescriptor('error.city_select_required'),
            );
        } else if (!value && required) {
            const messageId =
                postalCode && PostalCodeValidation.isOverSeasPostalCode(postalCode)
                    ? 'error.country_required'
                    : this.ERROR_REQUIRED_ID;
            return intl.formatMessage(TranslationHelpers.getMessageDescriptor(messageId));
        }

        let errorMessage: IErrorMessage = '';
        if (value) {
            const labelId =
                postalCode && PostalCodeValidation.isOverSeasPostalCode(postalCode)
                    ? 'label.country'
                    : this.LABEL_ID;
            const err = this.cityFieldValidator.validate(value, labelId);
            if (err !== null) {
                errorMessage = err.message;
            }
        }
        return errorMessage;
    }

    private resolveCitiesMap() {
        const { countryCode } = this.props;
        if (countryCode === 'TW') {
            this.citiesMap = TWCitiesMap;
        } else {
            this.citiesMap = undefined;
        }
    }

    private renderInputField({
        form: { touched, errors, setFieldValue, handleBlur },
    }: FieldProps): ReactNode {
        const { autoFocus, value, required, postalCode } = this.props;
        const isTouched = touched && touched[this.ID];
        const errorMessage = isTouched && errors && errors[this.ID] ? errors[this.ID] : undefined;
        const onBlur = (event: React.FocusEvent<any>) => {
            setFieldValue(this.ID, ValueFormatters.trimExtraSpaces(event.target.value));
            handleBlur(event);
        };

        const labelId =
            postalCode && PostalCodeValidation.isOverSeasPostalCode(postalCode)
                ? 'label.country'
                : this.LABEL_ID;
        return (
            <InputField
                autoComplete="address-level2"
                labelMessage={TranslationHelpers.getMessageDescriptor(labelId)}
                type="text"
                id={this.ID}
                maxLength={this.MAX_LENGTH}
                name={this.ID}
                value={value}
                error={errorMessage}
                autoFocus={autoFocus}
                required={required}
                onBlur={onBlur}
                className={this.props.className}
            />
        );
    }

    private renderSelectField({
        form: { touched, errors, setFieldValue, setFieldTouched },
    }: FieldProps) {
        const { value = '', code = '', state = '' } = this.props;
        const items = (this.citiesMap && this.citiesMap[state]) || [];
        const postalCode = items.find((item) => item.code === code)?.postalCode || '';
        const isTouched = touched && (touched[this.ID] || touched[this.ID]);
        const errorMessage = isTouched && errors && errors[this.ID] ? errors[this.ID] : '';
        const selectedItem = code
            ? ({ name: value, code: code, postalCode } as ICity)
            : items.find((item) => item.name === value) || { name: value, code, postalCode };

        if (!code && selectedItem.code) {
            setFieldValue('cityCode', selectedItem.code);
        }

        const onCitySelect = (c: ICity) => {
            setFieldValue('city', c.name);
            setFieldValue('cityCode', c.code);
            setFieldValue('postalCode', c.postalCode);
        };
        return (
            <SearchSelectImproved
                id={this.ID}
                autoComplete="address-level2"
                items={items}
                selectedItem={selectedItem}
                label={TranslationHelpers.getMessageDescriptor(this.LABEL_ID)}
                required={true}
                error={errorMessage}
                searchKey="name"
                onSelect={onCitySelect}
                qaId="city"
                selectedItemKey="code"
                setTouched={setFieldTouched}
            />
        );
    }
}

export default injectIntl(CityField);
